import { useTranslation } from "react-i18next";
import {
  EViewerMode,
  ProjectDetailContext,
} from "../Interfaces/ProjectDetailInterfaces";
import { Q3DTabPanel } from "../../Parts/Q3DTabPanel";
import { MeasurementTypeButtons } from "./MeasurementTypeButtons";
import ReplyIcon from "@mui/icons-material/Reply";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useContext, useEffect, useState } from "react";
import CalcContext from "../Contexts/CalcContext";
import { MeasurementType } from "../../../viewer/measurement/types";
import Q3DIconButton from "../../Parts/Q3DIconButton";
import { UpdateInfoContext } from "../Contexts/UpdateInfoContext";
import { UserSiteContext } from "../../../..";

type Props = {
  width?: string;
};

export const CalcTabPanel = (props: Props) => {
  const { currentViewerMode, projectDetail, viewer, detailTab } =
    useContext(ProjectDetailContext);
  const {
    onEditingMode,
    measurementType,
    setOnEditingMode,
    measurementResultEdit,
  } = useContext(CalcContext);
  const [clickCount, setClickCount] = useState<number>(0);
  const { t, i18n } = useTranslation();
  const { setUpdateAt, updateAt } = useContext(UpdateInfoContext);

  // onPointClickEvent
  const onPointClick = (e: any) => {
    const count = e?.detail?.count;
    setClickCount(count);
  };

  // useEffect(() => {
  //   document.addEventListener("onPointClick", onPointClick);
  //   return () => {
  //     document.removeEventListener("onPointClick", onPointClick);
  //   };
  // }, []);

  useEffect(() => {
    if (currentViewerMode !== EViewerMode.CALCULATION) return;

    document.addEventListener("onPointClick", onPointClick);

    return () => {
      setOnEditingMode(false);
      document.removeEventListener("onPointClick", onPointClick);
    };
  }, [currentViewerMode]);

  const releasePoints = () => {
    viewer.undoMeasurement();
  };

  const finishMeasurement = () => {
    // "Edit (Measurement Result)" (SC_QUICK-2187, QU3DW-1367): X restores the
    // panel position from before the edit (AC-6, AC-9) instead of cancelling
    // a measurement-point edit.
    if (measurementResultEdit?.active) {
      measurementResultEdit.onCancel?.();
      return;
    }

    if (onEditingMode) {
      setOnEditingMode(false);
      viewer.cancelEditingMeasurement();
      return;
    }
    for (let i = 0; i < clickCount; ++i) {
      viewer.undoMeasurement();
    }
  };

  const EndMeasurement = async () => {
    viewer.finishMeasurementCreation();
  };

  const StartMeasurement = () => {};

  const EndEditing = () => {
    // "Edit (Measurement Result)": Tick saves the panel position immediately
    // (AC-5) instead of committing a measurement-point edit.
    if (measurementResultEdit?.active) {
      measurementResultEdit.onConfirm?.();
      return;
    }

    setOnEditingMode(false);
    viewer.updateEditMeasurement(projectDetail.id, updateAt, setUpdateAt);
    viewer.finishMeasurement();
  };

  const IsDisablePointsButton = () => {
    return clickCount === 0;
  };

  const IsDisableCancelButton = () => {
    // AC-10: both buttons are disabled while the panel position is saving.
    if (measurementResultEdit?.active) return measurementResultEdit.saving;
    if (onEditingMode) return false;
    return clickCount === 0;
  };

  useEffect(() => {
    if (detailTab !== undefined) {
      setOnEditingMode(false);
    }
  }, [detailTab]);

  const IsDisableStartButton = () => {
    // VR-1 / AC-3 / AC-10: Tick only becomes active once the panel actually
    // moved, and is disabled again while the save is in flight.
    if (measurementResultEdit?.active) {
      return !measurementResultEdit.changed || measurementResultEdit.saving;
    }

    return (
      (clickCount < 3 &&
        (measurementType === MeasurementType.FLAT_AREA_CALCULATION ||
          measurementType === MeasurementType.VOLUME_CALCULATION) &&
        !onEditingMode) ||
      (clickCount < 2 &&
        measurementType === MeasurementType.MULTIPLE_POINT &&
        !onEditingMode) ||
      (measurementType !== MeasurementType.FLAT_AREA_CALCULATION &&
        measurementType !== MeasurementType.VOLUME_CALCULATION &&
        measurementType !== MeasurementType.MULTIPLE_POINT &&
        !onEditingMode)
    );
  };

  return (
    <Q3DTabPanel
      value={EViewerMode.CALCULATION}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "var(--space-space--4, 4px)",
        width: props.width,
        paddingLeft: "4px",
      }}
    >
      <MeasurementTypeButtons />
      <Q3DIconButton
        title={t("UNMARK")}
        titleType={i18n.language === "ja" ? "both" : "tooltip"}
        icon={<ReplyIcon />}
        height={"36px"}
        onClick={releasePoints}
        disabled={IsDisablePointsButton()}
        type={"warning"}
        tooltipHidden={
          !(
            measurementType === MeasurementType.FLAT_AREA_CALCULATION ||
            measurementType === MeasurementType.VOLUME_CALCULATION ||
            measurementType === MeasurementType.MULTIPLE_POINT
          )
        }
        sx={{
          minWidth: "36px",
          visibility:
            !(
              measurementType === MeasurementType.FLAT_AREA_CALCULATION ||
              measurementType === MeasurementType.VOLUME_CALCULATION ||
              measurementType === MeasurementType.MULTIPLE_POINT
            ) && "hidden",
        }}
      />
      <Q3DIconButton
        title={t("CANCEL")}
        titleType={i18n.language === "ja" ? "both" : "tooltip"}
        icon={<CloseIcon />}
        height={"36px"}
        onClick={finishMeasurement}
        disabled={IsDisableCancelButton()}
        type={"error"}
        sx={{ minWidth: "36px" }}
      />
      <Q3DIconButton
        title={t("CONFIRM")}
        titleType={i18n.language === "ja" ? "both" : "tooltip"}
        icon={<CheckIcon />}
        height={"36px"}
        onClick={
          onEditingMode
            ? EndEditing
            : measurementType === MeasurementType.FLAT_AREA_CALCULATION ||
              measurementType === MeasurementType.VOLUME_CALCULATION ||
              measurementType === MeasurementType.MULTIPLE_POINT
            ? EndMeasurement
            : StartMeasurement
        }
        disabled={IsDisableStartButton()}
        type={"default"}
        sx={{ minWidth: "36px" }}
      />
    </Q3DTabPanel>
  );
};
